import { ref, reactive, watch, computed } from 'vue';
import { getOSRSectorDensityData } from '@/services/data/graph-data.js'
import { loadSpeciesInfo, loadHabitatInfo, getGuilds } from '@/services/data/biobrowser-data.js'
import { getSpeciesProfileContentBySName } from '@/services/data/cms-species-content.js'
import { getAllPhotos } from '@/services/data/photo-library.js';
import { getSpeciesLayers, loadLayerData, getOSRHFLayerIds, getAreaSummary } from '@/services/data/map-portal.js';
import {
  occurrenceMapName, suitablityMapNames,
  getNameFromUrl, conservationReferenceDef, taxonomic,
  getSingleDisplaySpeciesName, getSpeciesNameByGroup,
  formatPhotoPath, tabDefinition, defaultTab,
  DATA_MODEL as dataModel, statusTabSections
}
  from '@/config/profile-config.js';
import { getBBox } from '@/components/map/map-util.js';

const AB_AOI_ID = 15; /* full alberta region id 15 */
const OSR_AOI_ID = 64; /* combined OSR region id 64 */
const appState = reactive({
});

export function useAppState () {
  return appState;
}

function app () {
  const state = useAppState();
  // Refs can be updated async
  const tsn = ref();
  const sppContent = reactive({});
  const currentTab = ref(defaultTab);
  const mapLayers = reactive([]);
  const photoCollection = reactive({});
  const introPhotoCount = ref(0);
  const introPhotoPaths = ref();
  const toShowOverviewTab = ref(false);
  const toShowOSRTab = ref(false);
  const hasAnyStatusTabSections = ref(true);
  const osrChartData = reactive([]);
  const allTabs = reactive(tabDefinition);

  const aoiData = reactive({}); // yes, need to be reactive as the aoiData will be load async
  const aoiOSRData = reactive({}); // yes, need to be reactive
  const osrHFLayers = ref(); // yes, need to be reactive
  const osrAreaSummary = ref();

  const guilds = reactive([]);
  const conservationReference = reactive([]);
  const profilePageUrl = window.location.origin + window.location.pathname + window.location.search;

  watch(() => state.photoName, async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      const data = await getAllPhotos(newVal, state.groupName);
      /* format data */
      // combind profile array into banner array
      if (data.profile) {
        data.banner = data.profile.concat(data.banner.filter((item) => !data.profile.find(p => p.id === item.id)));
        data.profile = [];
      }
      Object.keys(data || {}).forEach(key => {
        data[key].forEach(item => {
          item.path = formatPhotoPath(item, key.includes('tab-') ? 'tab' : key);
        });
      });

      Object.assign(photoCollection, data);
      // get photo credif from banner and profile images. pullout ignored for now
      const photoCredits = {};
      ['banner', 'profile', 'characteristics', 'habitat'].forEach(photoType => {
        photoCredits[photoType] = [];
        photoCollection[photoType]?.forEach(singlePhoto => {
          if (singlePhoto.credit && !photoCredits[photoType].includes(singlePhoto.credit)) {
            photoCredits[photoType].push(singlePhoto.credit);
          }
        })
        if (photoCredits[photoType].length === 0) {
          delete photoCredits[photoType]
        }
      });

      sppContent.photoCredits = photoCredits;
      // when only single photo available
      introPhotoCount.value += (photoCollection?.characteristics?.length > 0) ? 1 : 0;
      introPhotoCount.value += photoCollection?.habitat?.length > 0 ? 1 : 0;
      if (introPhotoCount.value === 1) {
        introPhotoPaths.value = photoCollection?.characteristics?.length > 0 ? photoCollection?.characteristics[0].path : (photoCollection?.habitat?.length > 0 ? photoCollection?.habitat[0].path : null);
      }
    }
  });
  const excludedList = []; // ['Badger', 'Pronghorn', 'Richardson\'s Ground Squirrel', 'White-tailed Jack Rabbit'];
  watch(() => state.tsn, (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      /* 2023, hide habitat element maps, will revert it back after the maps are properly loaded */
      (async () => {
        const data = await getSpeciesLayers(newVal, (state.isSpecies ? 'species' : 'habitat'));
        mapLayers.push(...data);
        // always have occurrent map, the rest may be exist
        state.hasOccurrenceMap = data?.some(x => x.name === occurrenceMapName);
        if (excludedList.includes(state.commonName)) { // skip the species lsit above and hide their maps.
          state.availabelStatusSection = [];
          allTabs.alberta.show = false;
          state.hasMap = false;
          // state.hasOccurrenceMap = false;
          // return;
          statusTabSections.forEach(cur => {
            state[cur[1]] = false;
          });
        } else {
          allTabs.alberta.show = true;
          state.hasMap = data?.some(x => x.name === suitablityMapNames[0]);
        }
        updateAvailableSection();
        if (state.hasMap || state.hasOccurrenceMap) {
          (async () => {
            /* get alberta boundary AOI */
            aoiData.aoiId = AB_AOI_ID;
            aoiData.data = await loadLayerData(aoiData.aoiId, true);
            aoiData.bbox = getBBox(aoiData.data.geom);
          })();
        }
      })();

      // state.availabelStatusSection = statusTabSections.filter(section => !!state[section[1]]).map(section => section[1]);
    }
  });

  watch(() => state.tsn, async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      const data = await getGuilds(state);
      const reference = new Set();
      (data ?? []).forEach(conservationItem => {
        /* save conservation status */
        guilds.push(conservationItem);
        /* get conservation status - named by config and content hard coded in web lanaguge */
        conservationItem?.data?.forEach(x => {
          if (conservationReferenceDef[x.name]) { // if constains a reference
            if (x.name === 'General Status AB') { // difference reference for different spp group
              if ([taxonomic.mammal, taxonomic.bird, taxonomic.amphibian].includes(state.groupName)) {
                reference.add(conservationReferenceDef[x.name][0]);
              } else if ([taxonomic.mite, taxonomic.moss, taxonomic.lichen, taxonomic.plant].includes(state.groupName)) { // bryophyte, lichen, soil mite, vascular plant
                reference.add(conservationReferenceDef[x.name][1]);
              }
            } else {
              reference.add(conservationReferenceDef[x.name]);
            }
          }
        });
      });
      /* add related reference link to conservation status */
      reference.forEach(x => { conservationReference.push(x); });
    }
  });

  /* if there is an OSR tab, get aoi of OSR */
  watch(() => state.hasOSR, (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      /** if mammal show mammal tab */
      if (state.groupName === taxonomic.mammal) {
      /* get osr data and decide to show the OSR tab or not */

        toShowOSRTab.value = true;
        allTabs.mammalOsr.show = true;
      } else if (state.groupName === taxonomic.bird) { // for birs
        /* get OSR  boundary AOI */
        (async () => {
          aoiOSRData.aoiId = OSR_AOI_ID;
          aoiOSRData.data = await loadLayerData(aoiOSRData.aoiId, true);
          aoiOSRData.bbox = getBBox(aoiOSRData.data.geom);
        })();

        /* get OSR reporting tab hf layer ids */
        (async () => {
          const data = await getOSRHFLayerIds(state.sppGroup.hfMapYear);
          osrHFLayers.value = data.osr;
          const allLayerIds = osrHFLayers.value.reduce((prev, x) => [...prev, ...x.ids], []);
          allLayerIds.push(data.hfi);
          const summaryData = await getAreaSummary(OSR_AOI_ID, allLayerIds);

          /*  package data */
          osrAreaSummary.value = {};
          let energyHF = 0;
          summaryData.forEach(item => {
            const hfLabel = osrHFLayers.value.find(x => x.ids.includes(item.datasetId))?.name.toLowerCase();
            if (hfLabel) {
              const pct = item.value;
              osrAreaSummary.value[hfLabel] = +((osrAreaSummary.value[hfLabel] ?? 0) + (+pct)).toFixed(2)
              energyHF += (+item.value);
            } else {
              if (item.datasetId === data.hfi) { // if it is a hfi layer
                osrAreaSummary.value.totalHF = (+item.value);
              }
            }
          });
          osrAreaSummary.value.energyHF = +(energyHF.toFixed(2));
        })();
        /* get osr data and decide to show the OSR tab or not */

        (async () => {
          // TODO: need to change back to state.year. 2023 no bird osr data, so use old version 2019
          const osrData = await getOSRSectorDensityData(state.tsn, 2019); // state.year); hard coded
          if (osrData && Object.keys(osrData).length > 0) {
            osrChartData.push(...osrData);
            toShowOSRTab.value = true;
            allTabs.osr.show = true;
          }
        })();
      }
    }
  });

  const hasNLSighting = computed(() => {
    // return state?.sightings?.length > 0
    return photoCollection?.naturelynx?.length > 0;
  });
  // Init loads anything needed at startup
  const init = async (isHabitat = false, tm, tme) => {
    Object.keys(allTabs).forEach(x => {
      allTabs[x].label = tm(`profile.topNav.tabs.${x}Full`);
      allTabs[x].shortLabel = tme(`profile.topNav.tabs.${x}Short`) ? tm(`profile.topNav.tabs.${x}Short`) : tm(`profile.topNav.tabs.${x}Full`);
    });

    state.isSpecies = !isHabitat;

    if (!isHabitat) {
      // tsn.value = pathNames.find(x => x );
      const sppName = getNameFromUrl('species/alder-flycatcher');

      /* species information: name, group, year etc. */
      const params = { sppName };
      const result = await loadSpeciesInfo(params);
      Object.keys(result || {}).forEach((key) => {
        state[key] = result[key];
      });
      state.photoName = state.scientificName;
      if (state.scientificName) {
        state.groupName = state?.sppGroup?.groupName;
        tsn.value = state?.tsn;
        /* here bird mammal use common name, the rest uses common name if avaialble, otherwise sci names */
        state.sppName = getSingleDisplaySpeciesName(state, state?.groupName);
        /* here bird mammal use common name, the rest uses sci names */
        state.sppNameByGroup = getSpeciesNameByGroup(state, state?.groupName);
        state.isBird = state?.sppGroup?.groupId === 1;
        state.year = state?.sppGroup?.releaseYear;
        const tmpContent = await getSpeciesProfileContentBySName(state.scientificName, state.groupName, state.isSpecies);
        for (const key in tmpContent) {
          tmpContent[key] = String(tmpContent[key]).replaceAll('{sppName}', state.sppName);
        }
        Object.assign(sppContent, { ...sppContent, ...tmpContent });
        /* when no content from species profile,
          or with profile, but missing both AppIdMe (charcteristic) and habitat info
        don't show overview page. */
        if (Object.keys(tmpContent).length === 0 ||
          !(tmpContent.AppIdMe || tmpContent.AppHabitat)) {
          allTabs.overview.show = false;
          toShowOverviewTab.value = false;
          currentTab.value = 'alberta';
        } else {
          toShowOverviewTab.value = true;
          allTabs.overview.show = true;
        }
      }
      if (excludedList.includes(state.commonName)) { // skip the species lsit above and hide their maps.
        state.hasHFSection = false
      } else {
        state.hasHFSection = (state.isForestModel === 1 || state.isPrairieModel === 1);
      }
    } else {
      /* habitat information */
      const habitatName = getNameFromUrl('habitat/canopy-closure');
      const params = { name: habitatName };
      const result = await loadHabitatInfo(params);

      if (result) {
        Object.keys(result.habitat || {}).forEach((key) => {
          state[key] = (result.habitat)[key];
        });
        state.sppGroup = result.sppGroup || {};
        state.sppName = state.name;
        state.commonName = state.name;
        state.scientificName = state.name;
        state.photoName = state.sppName;
        state.groupName = state?.sppGroup?.groupName;
        state.tsn = state.id; // use tsn as the id field name for both habitat and species
        state.year = state?.sppGroup?.releaseYear;
        const tmpContent = await getSpeciesProfileContentBySName(state.sppName, state.groupName, state.isSpecies);
        for (const key in tmpContent) {
          tmpContent[key] = tmpContent[key].replaceAll('{sppName}', state.sppName);
        }
        Object.assign(sppContent, { ...sppContent, ...tmpContent });
      }
      // state.hasHabitatSection = true;
      // state.hasHFSection = true;
      state.isForestModel = dataModel.full;
      state.isPrairieModel = dataModel.basic;
      state.hasHFSection = false;
    }
    if (excludedList.includes(state.commonName)) {
      state.hasHabitatSection = false;
    } else {
      state.hasHabitatSection = (state.isForestModel < 3 || state.isPrairieModel < 3);
    }
    updateAvailableSection();
    /* update document title after read scientific name and common name */
    document.title = `ABMI ${state.scientificName || ''} ${state.commonName || ''}`;
  };

  const updateAvailableSection = () => {
    state.availabelStatusSection = statusTabSections.filter(section => {
      return !!state[section[1]];
    }).map(section => section[1]);

    // for status tab, check if any section is available, and therefore use different section backgrounds
    hasAnyStatusTabSections.value = statusTabSections.reduce((pre, cur) => {
      return pre || !!state[cur[1]]
    }, false);

    if (!hasAnyStatusTabSections.value && allTabs.overview.show) {
      allTabs.alberta.show = false;
      currentTab.value = 'overview';
    } else {
      allTabs.alberta.show = true;
      currentTab.value = 'alberta';
    }
    // allTabs.alberta.show = hasAnyStatusTabSections.value;
    // console.log({ avail: state.availabelStatusSection, x: hasAnyStatusTabSections.value });
    // when no ab status available, to overview only.
    // if (!hasAnyStatusTabSections.value) {
    //   // currentTab.value = 'overview';
    //   if (!hasSppContent) {
    //     toShowOverviewTab.value = false;
    //     allTabs.alberta.show = true;
    //     currentTab.value = 'alberta';
    //   } else {
    //     toShowOverviewTab.value = true;
    //     allTabs.alberta.show = false;
    //     currentTab.value = 'overview';
    //   }
    // }
  }

  const tabs = computed(() => {
    return Object.entries(allTabs)
      .filter(e => e[1].show)
      .reduce((acc, entry) => {
        acc[entry[0]] = entry[1];
        return acc;
      }, {});
  });
  const switchTab = (tab) => {
    if (tab !== currentTab.value) {
      currentTab.value = tab;
      // (document.getDocu('profile') ?? document.getElementById('app')).scrollIntoView();
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }
  };

  const hasSppContent = computed(() => {
    return (sppContent?.AppIdMe?.trim().length > 0) || (sppContent?.habitat?.trim().length > 0)
  });
  const taxonCategroyName = computed(() => state.isSpecies ? 'species.' : 'habitat.');
  return {
    state,
    guilds,
    conservationReference,
    sppContent,
    currentTab,
    osrChartData,

    photoCollection,

    introPhotoCount,
    introPhotoPaths,
    mapLayers,
    aoiData,
    aoiOSRData,
    osrHFLayers,
    osrAreaSummary,
    switchTab,
    init,
    tabs,
    toShowOSRTab,
    toShowOverviewTab,
    hasAnyStatusTabSections,

    profilePageUrl,
    hasSppContent,
    hasNLSighting,
    taxonCategroyName
  };
}
const appSingleton = app();
export function useApp () {
  // NOTE: Any setup hooks must be called here.
  // This gets called from every setup() that uses the app.
  return appSingleton;
}
